import styles from "../UserAdminModal.module.css";
import { pickTextFamilyForBg, previewAccentHex } from "../../utils/theme";
import { useUserAdminContext } from "../../context/UserAdminContext";
import { buildImageSrc, BRAND_PRESETS } from "./userAdminHelpers";

// Extraído de UserAdminModal.jsx (issue 012, docs/07-issues.md) — bloco de
// branding (logo, logo variante clara, ícone PWA, cor de marca, fundo
// personalizado) que existia DUPLICADO quase por inteiro em dois pontos:
// admin editando a marca de um professor (aba "form") e o próprio professor
// editando a própria marca (aba "profile"). `mode` seleciona só as poucas
// diferenças de texto entre os dois contextos ("este professor" vs "você");
// o restante (JSX, regras de contraste, upload) é idêntico nos dois.
//
// Upload de logo/logo-claro/ícone usa estado JÁ COMPARTILHADO globalmente
// (logoFile/logoPreview/... vêm do contexto) — não havia duas cópias desse
// estado no arquivo original, só duas cópias do JSX que o exibia. Já a cor
// de marca/fundo É por-formulário (form.brand_color vs profileForm.brand_color),
// por isso entra via prop com o valor + callback de mudança de cada lado.
const COPY = {
  admin: {
    logoDesc: "Substitui o logo padrão em todos os modais para este professor e seus alunos.",
    logoLightDesc:
      "Usada no lugar do logo acima quando o aluno está no tema claro - mesma troca do wordmark padrão do TreinoNexo. Sem essa variante, o logo acima vale pros dois modos (comportamento de sempre). Só vale a pena se o logo principal tiver traço claro (ficaria pouco legível num fundo claro sem essa segunda versão).",
    iconDesc:
      'Usado quando o aluno instala o app ("Adicionar à tela inicial") no celular. Precisa ser quadrado - envie só o símbolo/marca, sem texto comprido, que vai ser centralizado num fundo com a cor de marca.',
    brandColorDesc: "Personaliza o acento visual em toda a interface para este professor e seus alunos. Escolha um tom pronto ou use o seletor para qualquer cor.",
    saveNote: "salve o formulário para aplicar",
    bgDesc:
      "Define como a marca aparece nos dois ambientes de treino do aluno: um valor exato pra cada modo do alternador escuro/claro (que continua funcionando - só a cor de base de cada lado muda). O texto se ajusta sozinho pra continuar legível em qualquer cor. Deixe em branco para manter o padrão do app nesse modo.",
  },
  self: {
    logoDesc: "Substitui o logo padrão em todos os modais para você e seus alunos.",
    logoLightDesc:
      "Usada no lugar do logo acima quando o aluno está no tema claro - mesma troca do wordmark padrão do TreinoNexo. Sem essa variante, o logo acima vale pros dois modos (comportamento de sempre). Só vale a pena se o seu logo tiver traço claro (ficaria pouco legível num fundo claro sem essa segunda versão).",
    iconDesc:
      'Usado quando o aluno instala o app ("Adicionar à tela inicial") no celular. Precisa ser quadrado - envie só o símbolo/marca, sem texto comprido, que vai ser centralizado num fundo com a sua cor de marca.',
    brandColorDesc: "Personaliza o acento visual em toda a interface para você e seus alunos. Escolha um tom pronto ou use o seletor para qualquer cor.",
    saveNote: "salve o perfil para aplicar",
    bgDesc:
      "Define como sua marca aparece nos dois ambientes de treino do aluno: um valor exato pra cada modo do alternador escuro/claro (que continua funcionando - só a cor de base de cada lado muda). O texto se ajusta sozinho pra continuar legível em qualquer cor. Deixe em branco para manter o padrão do app nesse modo.",
  },
};

export default function BrandingSection({
  mode, // "admin" | "self"
  owner, // editingUser (admin) ou currentUser (self) — dono do logo/ícone atuais
  brandColor,
  onBrandColorChange,
  bgColorDark,
  onBgColorDarkChange,
  bgColorLight,
  onBgColorLightChange,
}) {
  const {
    apiBase,
    logoFile, logoPreview, logoSaving,
    handleLogoFileChange, handleLogoUpload, handleLogoRemove,
    logoLightFile, logoLightPreview, logoLightSaving,
    handleLogoLightFileChange, handleLogoLightUpload, handleLogoLightRemove,
    appIconFile, appIconPreview, appIconSaving,
    handleAppIconFileChange, handleAppIconUpload, handleAppIconRemove,
  } = useUserAdminContext();

  const copy = COPY[mode];

  return (
    <>
      {/* ── Logo personalizado ── */}
      <div className={styles.uamLogoSection}>
        <h4 className={styles.uamSectionTitle}>Logo personalizado</h4>
        <p className={styles.uamHelper} style={{ marginBottom: 12 }}>
          {copy.logoDesc}
        </p>

        {(logoPreview || owner.custom_logo_url) && (
          <div className={styles.uamLogoPreview}>
            <img
              src={logoPreview || buildImageSrc(owner.custom_logo_url, apiBase)}
              alt="Logo preview"
            />
          </div>
        )}

        <div className={styles.uamLogoActions}>
          <label className={`${styles.uamBtn} ${styles.ghost}`} style={{ cursor: "pointer" }}>
            {logoFile ? "Trocar arquivo" : "Selecionar imagem"}
            <input
              type="file"
              accept="image/*"
              style={{ display: "none" }}
              onChange={handleLogoFileChange}
            />
          </label>

          {logoFile && (
            <button
              type="button"
              className={`${styles.uamBtn} ${styles.primary}`}
              onClick={handleLogoUpload}
              disabled={logoSaving}
            >
              {logoSaving ? "Salvando…" : "Salvar logo"}
            </button>
          )}

          {owner.custom_logo_url && !logoFile && (
            <button
              type="button"
              className={`${styles.uamBtn} ${styles.danger}`}
              onClick={handleLogoRemove}
              disabled={logoSaving}
            >
              Remover logo
            </button>
          )}
        </div>
      </div>

      {/* ── Logo (variante clara, opcional) — R248 ── */}
      <div className={styles.uamLogoSection}>
        <h4 className={styles.uamSectionTitle}>Logo - variante clara (opcional)</h4>
        <p className={styles.uamHelper} style={{ marginBottom: 12 }}>
          {copy.logoLightDesc}
        </p>

        {(logoLightPreview || owner.custom_logo_light_url) && (
          <div className={styles.uamLogoPreview}>
            <img
              src={logoLightPreview || buildImageSrc(owner.custom_logo_light_url, apiBase)}
              alt="Logo (variante clara) preview"
            />
          </div>
        )}

        <div className={styles.uamLogoActions}>
          <label className={`${styles.uamBtn} ${styles.ghost}`} style={{ cursor: "pointer" }}>
            {logoLightFile ? "Trocar arquivo" : "Selecionar imagem"}
            <input
              type="file"
              accept="image/*"
              style={{ display: "none" }}
              onChange={handleLogoLightFileChange}
            />
          </label>

          {logoLightFile && (
            <button
              type="button"
              className={`${styles.uamBtn} ${styles.primary}`}
              onClick={handleLogoLightUpload}
              disabled={logoLightSaving}
            >
              {logoLightSaving ? "Salvando…" : "Salvar logo claro"}
            </button>
          )}

          {owner.custom_logo_light_url && !logoLightFile && (
            <button
              type="button"
              className={`${styles.uamBtn} ${styles.danger}`}
              onClick={handleLogoLightRemove}
              disabled={logoLightSaving}
            >
              Remover logo claro
            </button>
          )}
        </div>
      </div>

      {/* ── Ícone do app (PWA) ── */}
      <div className={styles.uamLogoSection}>
        <h4 className={styles.uamSectionTitle}>Ícone do app (tela inicial)</h4>
        <p className={styles.uamHelper} style={{ marginBottom: 12 }}>
          {copy.iconDesc}
        </p>

        {(appIconPreview || owner.custom_app_icon_url) && (
          <div className={styles.uamLogoPreview}>
            <img
              src={appIconPreview || buildImageSrc(owner.custom_app_icon_url, apiBase)}
              alt="Ícone do app preview"
              style={{ width: 64, height: 64, borderRadius: 16, objectFit: "cover" }}
            />
          </div>
        )}

        <div className={styles.uamLogoActions}>
          <label className={`${styles.uamBtn} ${styles.ghost}`} style={{ cursor: "pointer" }}>
            {appIconFile ? "Trocar arquivo" : "Selecionar imagem"}
            <input
              type="file"
              accept="image/*"
              style={{ display: "none" }}
              onChange={handleAppIconFileChange}
            />
          </label>

          {appIconFile && (
            <button
              type="button"
              className={`${styles.uamBtn} ${styles.primary}`}
              onClick={handleAppIconUpload}
              disabled={appIconSaving}
            >
              {appIconSaving ? "Salvando…" : "Salvar ícone"}
            </button>
          )}

          {owner.custom_app_icon_url && !appIconFile && (
            <button
              type="button"
              className={`${styles.uamBtn} ${styles.danger}`}
              onClick={handleAppIconRemove}
              disabled={appIconSaving}
            >
              Remover ícone
            </button>
          )}
        </div>
      </div>

      {/* ── Cor de marca ── */}
      <div className={styles.uamLogoSection}>
        <h4 className={styles.uamSectionTitle}>Cor de marca</h4>
        <p className={styles.uamHelper} style={{ marginBottom: 12 }}>
          {copy.brandColorDesc}
        </p>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginBottom: 12 }}>
          {BRAND_PRESETS.map(({ hex, label }) => {
            const active = brandColor === hex;
            return (
              <button
                key={hex}
                type="button"
                title={label}
                onClick={() => onBrandColorChange(hex)}
                style={{
                  width: 36, height: 36, borderRadius: "50%",
                  background: hex, border: active ? "3px solid #fff" : "3px solid transparent",
                  outline: active ? `3px solid ${hex}` : "none",
                  outlineOffset: 2,
                  cursor: "pointer", transition: "transform .15s",
                  transform: active ? "scale(1.18)" : "scale(1)",
                }}
              />
            );
          })}
          {/* Cor livre - qualquer hex via seletor nativo */}
          <label
            title="Cor personalizada"
            style={{
              width: 36, height: 36, borderRadius: "50%",
              background: "conic-gradient(#f43f5e, #eab308, var(--feedback-success), #06b6d4, #6366f1, #d946ef, #f43f5e)",
              border: brandColor && !BRAND_PRESETS.some(p => p.hex === brandColor)
                ? "3px solid #fff" : "3px solid transparent",
              cursor: "pointer", position: "relative", overflow: "hidden",
              display: "inline-block", flexShrink: 0,
            }}
          >
            <input
              type="color"
              value={/^#[0-9A-Fa-f]{6}$/.test(brandColor || "") ? brandColor : "#1d9e75"}
              onChange={(e) => onBrandColorChange(e.target.value)}
              style={{ position: "absolute", inset: 0, opacity: 0, width: "100%", height: "100%", cursor: "pointer" }}
            />
          </label>
          {brandColor && (
            <button
              type="button"
              title="Remover cor personalizada"
              onClick={() => onBrandColorChange("")}
              style={{
                width: 36, height: 36, borderRadius: "50%",
                background: "rgba(255,255,255,0.08)",
                border: "2px dashed rgba(255,255,255,0.25)",
                color: "var(--text-0-50)", fontSize: 18, lineHeight: 1,
                cursor: "pointer",
              }}
            >×</button>
          )}
        </div>
        {brandColor && (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <span style={{ fontSize: 13, color: "var(--text-0-60)" }}>
              {BRAND_PRESETS.find(p => p.hex === brandColor)?.label || brandColor}
              {" "}- {copy.saveNote}
            </span>
            {/^#[0-9A-Fa-f]{6}$/.test(brandColor) && (
              <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                <span style={{ fontSize: 11, color: "var(--text-0-50)" }}>Prévia:</span>
                {[
                  { key: "dark", label: "escuro", bg: bgColorDark },
                  { key: "light", label: "claro", bg: bgColorLight },
                ].map(({ key, label, bg }) => {
                  const previewHex = previewAccentHex(brandColor, key, bg) || brandColor;
                  const adjusted = previewHex.toLowerCase() !== brandColor.toLowerCase();
                  return (
                    <span key={key} style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 11, color: "var(--text-0-60)" }}>
                      <span
                        title={adjusted ? `Ajustada automaticamente pra manter contraste legível no modo ${label}` : `Como vai aparecer no modo ${label}`}
                        style={{
                          width: 20, height: 20, borderRadius: "50%", background: previewHex,
                          border: "1px solid var(--border-strong)", display: "inline-block",
                        }}
                      />
                      {label}{adjusted && <span style={{ color: "var(--text-0-50)" }}>*</span>}
                    </span>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Fundo personalizado ── */}
      <div className={styles.uamLogoSection}>
        <h4 className={styles.uamSectionTitle}>Fundo personalizado (opcional)</h4>
        <p className={styles.uamHelper} style={{ marginBottom: 12 }}>
          {copy.bgDesc}
        </p>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
          <label
            title="Escolher cor"
            style={{
              width: 32, height: 32, borderRadius: 8,
              background: /^#[0-9A-Fa-f]{6}$/.test(bgColorDark || "") ? bgColorDark : "var(--surface-tertiary)",
              border: "2px solid var(--border-strong)",
              cursor: "pointer", position: "relative", overflow: "hidden", flexShrink: 0,
            }}
          >
            <input
              type="color"
              value={/^#[0-9A-Fa-f]{6}$/.test(bgColorDark || "") ? bgColorDark : "#000000"}
              onChange={(e) => onBgColorDarkChange(e.target.value)}
              style={{ position: "absolute", inset: 0, opacity: 0, width: "100%", height: "100%", cursor: "pointer" }}
            />
          </label>
          <span style={{ fontSize: 13, color: "var(--text-0-60)", minWidth: 140 }}>Fundo no modo escuro</span>
          <span style={{ fontSize: 12, color: "var(--text-0-50)", fontFamily: "monospace" }}>
            {bgColorDark || "padrão do app"}
          </span>
          {/^#[0-9A-Fa-f]{6}$/.test(bgColorDark || "") && (
            <span
              title={`Prévia - texto ${pickTextFamilyForBg(bgColorDark).family === "dark" ? "claro" : "escuro"} aplicado automaticamente pra manter o contraste`}
              style={{
                display: "inline-flex", alignItems: "center", justifyContent: "center",
                width: 26, height: 26, borderRadius: 6, fontSize: 11, fontWeight: 700,
                background: bgColorDark,
                color: pickTextFamilyForBg(bgColorDark).textHex,
                border: "1px solid var(--border-strong)", flexShrink: 0,
              }}
            >Aa</span>
          )}
          {bgColorDark && (
            <button
              type="button"
              title="Remover"
              aria-label="Remover cor de fundo do modo escuro"
              onClick={() => onBgColorDarkChange("")}
              style={{
                width: 26, height: 26, borderRadius: "50%",
                background: "rgba(255,255,255,0.08)",
                border: "2px dashed rgba(255,255,255,0.25)",
                color: "var(--text-0-50)", fontSize: 14, lineHeight: 1,
                cursor: "pointer", marginLeft: "auto",
              }}
            >×</button>
          )}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
          <label
            title="Escolher cor"
            style={{
              width: 32, height: 32, borderRadius: 8,
              background: /^#[0-9A-Fa-f]{6}$/.test(bgColorLight || "") ? bgColorLight : "var(--surface-tertiary)",
              border: "2px solid var(--border-strong)",
              cursor: "pointer", position: "relative", overflow: "hidden", flexShrink: 0,
            }}
          >
            <input
              type="color"
              value={/^#[0-9A-Fa-f]{6}$/.test(bgColorLight || "") ? bgColorLight : "#000000"}
              onChange={(e) => onBgColorLightChange(e.target.value)}
              style={{ position: "absolute", inset: 0, opacity: 0, width: "100%", height: "100%", cursor: "pointer" }}
            />
          </label>
          <span style={{ fontSize: 13, color: "var(--text-0-60)", minWidth: 140 }}>Fundo no modo claro</span>
          <span style={{ fontSize: 12, color: "var(--text-0-50)", fontFamily: "monospace" }}>
            {bgColorLight || "padrão do app"}
          </span>
          {/^#[0-9A-Fa-f]{6}$/.test(bgColorLight || "") && (
            <span
              title={`Prévia - texto ${pickTextFamilyForBg(bgColorLight).family === "dark" ? "claro" : "escuro"} aplicado automaticamente pra manter o contraste`}
              style={{
                display: "inline-flex", alignItems: "center", justifyContent: "center",
                width: 26, height: 26, borderRadius: 6, fontSize: 11, fontWeight: 700,
                background: bgColorLight,
                color: pickTextFamilyForBg(bgColorLight).textHex,
                border: "1px solid var(--border-strong)", flexShrink: 0,
              }}
            >Aa</span>
          )}
          {bgColorLight && (
            <button
              type="button"
              title="Remover"
              aria-label="Remover cor de fundo do modo claro"
              onClick={() => onBgColorLightChange("")}
              style={{
                width: 26, height: 26, borderRadius: "50%",
                background: "rgba(255,255,255,0.08)",
                border: "2px dashed rgba(255,255,255,0.25)",
                color: "var(--text-0-50)", fontSize: 14, lineHeight: 1,
                cursor: "pointer", marginLeft: "auto",
              }}
            >×</button>
          )}
        </div>
      </div>
    </>
  );
}
